import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import './AppStorePromo.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/versa-portal/id6762314679';
const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.versalaunch.client';
const EASE = [0.22, 0.61, 0.36, 1];

/**
 * App launch card. Monochrome and restrained - no gold, no blinking dot.
 * Now covers both stores: Apple App Store + Google Play, side by side.
 *
 * Two layouts from one component, switched by CSS on `variant`:
 *   - "stage"  vertical card that floats in the open gold space to the right of
 *              the hero phone (desktop only): icon + copy + both store badges +
 *              a QR for each store.
 *   - "bar"    compact icon + title + both badges for the top of the hero on
 *              tablet/phone, where there's no open space to fill. No QR - you
 *              can't scan the screen you're holding, the badges are tap links.
 *
 * Each store badge is its own link to its listing.
 */
const AppStorePromo = ({ variant = 'bar' }) => {
  const reduce = useReducedMotion();
  const isStage = variant === 'stage';

  return (
    <motion.div
      className={`as-promo-wrap as-promo-wrap--${variant}`}
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: EASE, delay: isStage ? 0.5 : 0 }}
    >
      <div className="as-promo">
        <div className="as-promo-head">
          <span className="as-promo-icon" aria-hidden="true">
            <img src="/favicons/apple-touch-icon.png" alt="" />
          </span>

          <span className="as-promo-copy">
            <span className="as-promo-title">Get the Versa Portal app</span>
            <span className="as-promo-sub">Free on iPhone, iPad &amp; Android</span>
          </span>
        </div>

        <div className="as-promo-badges">
          <a
            className="as-promo-badge"
            href={APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="Download Versa Portal on the Apple App Store"
          >
            <svg className="as-promo-glyph" viewBox="0 0 384 512" role="img" aria-hidden="true">
              <path
                fill="currentColor"
                d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
              />
            </svg>
            <span className="as-promo-badge-text">
              <small>Download on the</small>
              <strong>App Store</strong>
            </span>
          </a>

          <a
            className="as-promo-badge"
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="Get Versa Portal on Google Play"
          >
            <svg className="as-promo-glyph" viewBox="0 0 512 512" role="img" aria-hidden="true">
              <path
                fill="currentColor"
                d="M325.3 234.3 104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"
              />
            </svg>
            <span className="as-promo-badge-text">
              <small>GET IT ON</small>
              <strong>Google Play</strong>
            </span>
          </a>
        </div>

        <div className="as-promo-qr" aria-hidden="true">
          <span className="as-promo-qr-item">
            <span className="as-promo-qr-frame">
              <img src="/app-store-qr.svg" alt="" width="72" height="72" loading="lazy" />
            </span>
            <span className="as-promo-qr-cap">App Store</span>
          </span>
          <span className="as-promo-qr-item">
            <span className="as-promo-qr-frame">
              <img src="/google-play-qr.svg" alt="" width="72" height="72" loading="lazy" />
            </span>
            <span className="as-promo-qr-cap">Google Play</span>
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default AppStorePromo;
